'use client'

import { StaticPageForm } from '@/components/static-pages/StaticPageForm'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { StaticPage, StaticPageFormData } from '@/types/static-page'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const EditStaticPagePage: React.FC = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [page, setPage] = useState<StaticPage | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const loadPage = async () => {
      if (!id) return

      setIsLoading(true)
      try {
        const response = await apiClient.getStaticPage(id)
        setPage(response.data)
      } catch (error) {
        console.error('Error loading static page:', error)
        toast({
          title: 'Error',
          description: 'Failed to load static page. Please try again.',
          variant: 'destructive',
        })
        navigate('/static-pages')
      } finally {
        setIsLoading(false)
      }
    }

    loadPage()
  }, [id, navigate, toast])

  const handleFormSubmit = async (data: StaticPageFormData) => {
    if (!id) return

    setIsSubmitting(true)
    try {
      await apiClient.updateStaticPage(id, data)
      toast({
        title: 'Success',
        description: 'Static page updated successfully',
      })
      navigate('/static-pages')
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update static page. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFormCancel = () => {
    navigate('/static-pages')
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-9 w-64 mb-2" />
          <Skeleton className="h-5 w-96" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Edit Static Page</h1>
        <p className="text-muted-foreground">Update static content page</p>
      </div>

      <StaticPageForm
        page={page}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
        isLoading={isSubmitting}
      />
    </div>
  )
}

export default EditStaticPagePage
